/**
 * LangGraph 진행 시각화 (계획서 §8.1, §13.5).
 *
 * §8.1 의 node 순서를 SVG `viewBox` 로 그린다. 텍스트 목록만으로는 어디까지 갔는지
 * 알기 어려워 2026-09-04 에 도식으로 바꿨다.
 *
 * 설비 Agent 5개는 모두 병렬이다. 집진기는 건조기 **설비**의 일정을 따르지만 건조기
 * **Agent** 의 설명을 기다리지 않는다 — 필요한 일정은 결정론적 계산에서 직접 온다.
 *
 * 상태는 색상만으로 구분하지 않고 icon 과 text 를 함께 얹는다 (§13.1).
 * 선택한 node 의 input/output JSON 은 기본 접힘이다 (§13.5).
 */

import type { WorkflowNodeView, WorkflowResponse } from "@/types/api";

/** §8.1 의 node 순서. 좌표는 `viewBox` 기준이다. */
interface Slot {
  node: string;
  label: string;
  x: number;
  y: number;
}

const COLUMN = 150;
const BOX_W = 132;
const BOX_H = 46;

const SLOTS: Slot[] = [
  { node: "load_run_context", label: "context 적재", x: 10, y: 118 },
  { node: "calculate_deterministic_facts", label: "결정론적 사실", x: 10 + COLUMN, y: 118 },
  { node: "production_observer_subgraph", label: "Production Observer", x: 10 + COLUMN * 2, y: 118 },
  // 설비 Agent 5개는 같은 열에 나란히 둔다. 모두 병렬이다 (§8.1).
  { node: "welder_agent_subgraph", label: "용접기", x: 10 + COLUMN * 3, y: 10 },
  { node: "epoxy_agent_subgraph", label: "에폭시", x: 10 + COLUMN * 3, y: 64 },
  { node: "dryer_agent_subgraph", label: "건조기", x: 10 + COLUMN * 3, y: 118 },
  { node: "dust_collector_agent_subgraph", label: "집진기", x: 10 + COLUMN * 3, y: 172 },
  { node: "compressor_agent_subgraph", label: "컴프레서", x: 10 + COLUMN * 3, y: 226 },
  { node: "validate_agent_outputs", label: "출력 검증", x: 10 + COLUMN * 4, y: 118 },
  { node: "calculate_strategy_candidates", label: "A/B 후보", x: 10 + COLUMN * 5, y: 118 },
  { node: "status_center_subgraph", label: "Status Center", x: 10 + COLUMN * 6, y: 118 },
  { node: "persist_plan", label: "계획 저장", x: 10, y: 300 },
  { node: "interrupt_plan_approval", label: "① 계획 승인", x: 10 + COLUMN, y: 300 },
  { node: "interrupt_human_start", label: "② 인간 가동", x: 10 + COLUMN * 2, y: 300 },
  { node: "dispatch_mock_plc", label: "PLC 전송", x: 10 + COLUMN * 3, y: 300 },
  { node: "wait_mock_plc_ack", label: "ACK 대기", x: 10 + COLUMN * 4, y: 300 },
  { node: "activate_plan", label: "계획 활성화", x: 10 + COLUMN * 5, y: 300 },
];

const EDGES: [string, string][] = [
  ["load_run_context", "calculate_deterministic_facts"],
  ["calculate_deterministic_facts", "production_observer_subgraph"],
  ["production_observer_subgraph", "welder_agent_subgraph"],
  ["production_observer_subgraph", "epoxy_agent_subgraph"],
  ["production_observer_subgraph", "dryer_agent_subgraph"],
  ["production_observer_subgraph", "dust_collector_agent_subgraph"],
  ["production_observer_subgraph", "compressor_agent_subgraph"],
  ["welder_agent_subgraph", "validate_agent_outputs"],
  ["epoxy_agent_subgraph", "validate_agent_outputs"],
  ["dryer_agent_subgraph", "validate_agent_outputs"],
  ["dust_collector_agent_subgraph", "validate_agent_outputs"],
  ["compressor_agent_subgraph", "validate_agent_outputs"],
  ["validate_agent_outputs", "calculate_strategy_candidates"],
  ["calculate_strategy_candidates", "status_center_subgraph"],
  ["persist_plan", "interrupt_plan_approval"],
  ["interrupt_plan_approval", "interrupt_human_start"],
  ["interrupt_human_start", "dispatch_mock_plc"],
  ["dispatch_mock_plc", "wait_mock_plc_ack"],
  ["wait_mock_plc_ack", "activate_plan"],
];

type Phase = "done" | "running" | "waiting" | "failed" | "idle";

const PHASE_ICON: Record<Phase, string> = {
  done: "✔",
  running: "◌",
  waiting: "⏸",
  failed: "✕",
  idle: "○",
};

const PHASE_LABEL: Record<Phase, string> = {
  done: "완료",
  running: "실행 중",
  waiting: "사람 대기",
  failed: "실패",
  idle: "대기",
};

function phaseOf(slot: Slot, data: WorkflowResponse): Phase {
  const agent = data.nodes.find((n) => n.node === slot.node);
  if (agent) {
    if (agent.status === "COMPLETE") return "done";
    if (agent.status === "ERROR") return "failed";
    if (agent.status === "RUNNING") return "running";
    return "idle";
  }
  if (data.next.includes(slot.node)) {
    // interrupt node 에서 멈춰 있으면 사람의 결정을 기다리는 중이다 (§8.13).
    return slot.node.startsWith("interrupt_") ? "waiting" : "running";
  }
  return completedTailPhase(slot, data);
}

function completedTailPhase(slot: Slot, data: WorkflowResponse): Phase {
  switch (slot.node) {
    case "persist_plan":
      return data.nodes.length > 0 ? "done" : "idle";
    case "interrupt_plan_approval":
      return data.plan_decision && Object.keys(data.plan_decision).length > 0 ? "done" : "idle";
    case "interrupt_human_start":
    case "dispatch_mock_plc":
      return data.human_start && Object.keys(data.human_start).length > 0 ? "done" : "idle";
    case "wait_mock_plc_ack":
      return data.plc_ack && Object.keys(data.plc_ack).length > 0 ? "done" : "idle";
    case "activate_plan":
      return data.activated ? "done" : "idle";
    default:
      return "idle";
  }
}

function center(slot: Slot): { x: number; y: number } {
  return { x: slot.x + BOX_W / 2, y: slot.y + BOX_H / 2 };
}

interface Props {
  data: WorkflowResponse;
  selected: string | null;
  onSelect: (node: string) => void;
  /** 좁은 자리에서는 선택한 node 의 JSON 을 접는다. */
  compact?: boolean;
}

export function WorkflowGraph({
  data,
  selected,
  onSelect,
  compact = false,
}: Props): React.JSX.Element {
  const bySlot = new Map(SLOTS.map((slot) => [slot.node, slot]));
  const detail: WorkflowNodeView | undefined = data.nodes.find((n) => n.node === selected);

  return (
    <div className={compact ? "workflow-graph is-compact" : "workflow-graph"}>
      <svg viewBox="0 0 1060 360" role="img" aria-label="LangGraph 진행 상태">
        <defs>
          <marker
            id="wf-arrow"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" className="diagram-arrow-head" />
          </marker>
        </defs>

        {EDGES.map(([from, to]) => {
          const a = bySlot.get(from);
          const b = bySlot.get(to);
          if (!a || !b) return null;
          const start = center(a);
          const end = center(b);
          return (
            <line
              key={`${from}->${to}`}
              x1={start.x + BOX_W / 2}
              y1={start.y}
              x2={end.x - BOX_W / 2}
              y2={end.y}
              className="diagram-arrow"
              markerEnd="url(#wf-arrow)"
            />
          );
        })}

        {SLOTS.map((slot) => {
          const phase = phaseOf(slot, data);
          const isSelected = selected === slot.node;
          return (
            <g
              key={slot.node}
              className={`wf-node wf-${phase}${isSelected ? " is-selected" : ""}`}
              transform={`translate(${String(slot.x)}, ${String(slot.y)})`}
              role="button"
              tabIndex={0}
              aria-label={`${slot.label} ${PHASE_LABEL[phase]}`}
              onClick={() => {
                onSelect(slot.node);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") onSelect(slot.node);
              }}
            >
              <rect width={BOX_W} height={BOX_H} rx={8} className="wf-box" />
              <text x={9} y={19} className="wf-label">
                {slot.label}
              </text>
              <text x={9} y={36} className="wf-phase">
                {PHASE_ICON[phase]} {PHASE_LABEL[phase]}
              </text>
            </g>
          );
        })}
      </svg>

      {detail && !compact && (
        <pre className="json-panel">{JSON.stringify(detail, null, 2)}</pre>
      )}
    </div>
  );
}
